// VowAI Wedding Planner - Production Stable Version
import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// Trigger deployment - reverted to timezone fix version
import Navbar from './components/Navbar';
import VowAILabs from './components/VowAILabs';
import MobileBanner from './components/MobileBanner';
import VowBot from './components/VowBot';
import ScrollToTop from './components/ScrollToTop';
import SessionTracker from './utils/sessionTracker';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import VendorDashboard from './pages/VendorDashboard';
import ThemesDashboard from './pages/ThemesDashboard';
import TimelineDashboard from './pages/TimelineDashboard';
import Profile from './pages/Profile';

// Wedding Blueprint Pages
import DreamWeddingGenerator from './pages/WeddingBlueprint/DreamWeddingGenerator';
import MoodboardMatcher from './pages/WeddingBlueprint/MoodboardMatcher';
import BudgetAllocator from './pages/WeddingBlueprint/BudgetAllocator';
import BudgetManagement from './pages/BudgetManagement';

// Vendor Vault Pages
import SmartVendorMatching from './pages/VendorVault/SmartVendorMatching';
import VendorShortlistingAssistant from './pages/VendorVault/VendorShortlistingAssistant';

// WedCraft Studio Pages
import InviteGenerator from './pages/WedCraftStudio/InviteGenerator';
import CulturalRitualExplainer from './pages/WedCraftStudio/CulturalRitualExplainer';
import Invitations from './pages/Invitations';
import RitualsDashboard from './pages/RitualsDashboard';

// The Big Day Playbook Pages
import TimelineGenerator from './pages/BigDayPlaybook/TimelineGenerator';
import WeatherRiskAdvisor from './pages/BigDayPlaybook/WeatherRiskAdvisor';

// Ever After Memories Pages
import ThankYouNoteGenerator from './pages/EverAfterMemories/ThankYouNoteGenerator';
import MemoryCapsuleCreator from './pages/EverAfterMemories/MemoryCapsuleCreator';

// Admin Pages
import VowAILabsPage from './pages/Admin/VowAILabsPage';
import ResetPassword from './pages/ResetPassword';
import EvaluationDashboard from './pages/Admin/EvaluationDashboard';
import ABTestingDashboard from './pages/Admin/ABTestingDashboard';

import './index.css';

// Session tracking hook — must be inside Router for useLocation
function SessionTrackingProvider() {
  const location = useLocation();
  const trackerRef = useRef(null);

  useEffect(() => {
    trackerRef.current = new SessionTracker();

    // Set user if already logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        trackerRef.current.setUser(payload.userId);
      } catch (e) { /* ignore invalid token */ }
    }

    return () => trackerRef.current?.destroy();
  }, []);

  // Track route changes
  useEffect(() => {
    trackerRef.current?.onRouteChange(location.pathname);
  }, [location.pathname]);

  // Listen for login/logout
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          try {
            const payload = JSON.parse(atob(e.newValue.split('.')[1]));
            trackerRef.current?.setUser(payload.userId);
          } catch (err) { /* ignore */ }
        } else {
          trackerRef.current?.setUser(null);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return null;
}

function App() {
  return (
    <Router>
      <SessionTrackingProvider />
      <ScrollToTop />
      <div className="App min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
        <Navbar />
        <main className="container mx-auto px-4 py-8 pb-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/vendors" element={<VendorDashboard />} />
            <Route path="/dashboard/themes" element={<ThemesDashboard />} />
            <Route path="/dashboard/budget" element={<BudgetManagement />} />
            <Route path="/dashboard/invitations" element={<Invitations />} />
            <Route path="/dashboard/rituals" element={<RitualsDashboard />} />
            <Route path="/dashboard/timeline" element={<TimelineDashboard />} />
            <Route path="/profile" element={<Profile />} />
            
            {/* Wedding Blueprint Routes */}
            <Route path="/wedding-blueprint/dream-wedding-generator" element={<DreamWeddingGenerator />} />
            <Route path="/wedding-blueprint/moodboard-matcher" element={<MoodboardMatcher />} />
            <Route path="/wedding-blueprint/budget-allocator" element={<BudgetAllocator />} />
            
            {/* Vendor Vault Routes */}
            <Route path="/vendor-vault/smart-vendor-matching" element={<SmartVendorMatching />} />
            <Route path="/vendor-vault/vendor-shortlisting-assistant" element={<VendorShortlistingAssistant />} />
            
            {/* WedCraft Studio Routes */}
            <Route path="/wedcraft-studio/invite-generator" element={<InviteGenerator />} />
            <Route path="/wedcraft-studio/cultural-ritual-explainer" element={<CulturalRitualExplainer />} />
            
            {/* The Big Day Playbook Routes */}
            <Route path="/big-day-playbook/timeline-generator" element={<TimelineGenerator />} />
            <Route path="/big-day-playbook/weather-risk-advisor" element={<WeatherRiskAdvisor />} />
            
            {/* Ever After Memories Routes */}
            <Route path="/ever-after-memories/thank-you-note-generator" element={<ThankYouNoteGenerator />} />
            <Route path="/ever-after-memories/memory-capsule-creator" element={<MemoryCapsuleCreator />} />
            
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Admin Routes */}
            <Route path="/admin/vowai-labs" element={<VowAILabsPage />} />
            <Route path="/admin/evaluation" element={<EvaluationDashboard />} />
            <Route path="/admin/ab-testing" element={<ABTestingDashboard />} />
          </Routes>
        </main>
        
        {/* Mobile recommendation banner */}
        <MobileBanner />
        
        {/* VowAI Labs - Floating Widget visible on all pages */}
        <VowAILabs />
        
        {/* VowBot - Floating Chat Widget visible on all pages */}
        <VowBot />
      </div>
    </Router>
  );
}

export default App;
